import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_FEATURE_BANNERS } from "../../graphql/feature-banners";
import { DELETE_FEATURE_BANNER } from "../../graphql/feature-banners";
import adminClient from "../../graphql/admin-client";
import {
  StyledMainHeaderWrapper,
  StyledMainHeading,
  StyledTable,
  StyledTableRow,
  StyledTableHeader,
  StyledTableCell,
  StyledPlatformBadge,
  StyledActionButtons,
  StyledEmptyState,
  StyledBannerContainer
} from "./styles";
import BannerFormModal from "./banner-form-modal";
import {
  RotaButton,
  RotaButtonIcon,
  RotaStatusLabel
} from "@teamrota/rota-design";
import { StyledMainDetail } from "../styles";
import asyncConfirm from "~/src/utils/async-confirm";

const FeatureBanners = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const { data, loading, refetch } = useQuery(GET_FEATURE_BANNERS, {
    client: adminClient
  });

  const [deleteBanner] = useMutation(DELETE_FEATURE_BANNER, {
    client: adminClient,
    onCompleted: () => refetch()
  });

  const handleCreate = () => {
    setEditingBanner(null);
    setIsModalOpen(true);
  };

  const handleEdit = banner => {
    setEditingBanner(banner);
    setIsModalOpen(true);
  };

  const handleDelete = async id => {
    if (
      await asyncConfirm("Do you want to delete this feature banner?", {
        shouldHideSubText: true,
        shouldAddPadding: true,
        confirmButtonText: "Delete",
        falseButtonText: "Cancel"
      })
    ) {
      try {
        await deleteBanner({ variables: { id } });
      } catch (error) {
        alert("Error deleting banner: " + error.message);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingBanner(null);
  };

  const handleSaveSuccess = () => {
    refetch();
    handleModalClose();
  };

  const formatDate = date => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getStatusType = status => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "DRAFT":
        return "warning";
      case "PAUSED":
        return "warning";
      default:
        return "warning";
    }
  };

  const banners = data?.featureBanners || [];

  return (
    <StyledBannerContainer>
      <StyledMainHeaderWrapper>
        <StyledMainHeading>Feature Banners</StyledMainHeading>
        <RotaButtonIcon icon="add" onClick={handleCreate} />
      </StyledMainHeaderWrapper>

      {loading ? (
        <div>Loading...</div>
      ) : banners.length === 0 ? (
        <StyledEmptyState>
          <StyledMainDetail>No feature banners yet.</StyledMainDetail>
          <RotaButton onClick={handleCreate}>Create</RotaButton>
        </StyledEmptyState>
      ) : (
        <StyledTable>
          <thead>
            <tr>
              <StyledTableHeader>ID</StyledTableHeader>
              <StyledTableHeader>Status</StyledTableHeader>
              <StyledTableHeader>Audience</StyledTableHeader>
              <StyledTableHeader>Message</StyledTableHeader>
              <StyledTableHeader>Platforms</StyledTableHeader>
              <StyledTableHeader>Variant</StyledTableHeader>
              <StyledTableHeader>Starts At</StyledTableHeader>
              <StyledTableHeader>Ends At</StyledTableHeader>
              <StyledTableHeader>Actions</StyledTableHeader>
            </tr>
          </thead>
          <tbody>
            {banners.map(banner => (
              <StyledTableRow key={banner.id}>
                <StyledTableCell>{banner.id}</StyledTableCell>
                <StyledTableCell>
                  <RotaStatusLabel
                    style={{ width: "100%", fontSize: "12px" }}
                    type={getStatusType(banner.status)}
                  >
                    {banner.status}
                  </RotaStatusLabel>
                </StyledTableCell>
                <StyledTableCell>
                  {banner.audience === "ALL" && "All Candidates"}
                  {banner.audience === "CANDIDATES_WITHOUT_PREFERENCES" &&
                    "Without Preferences"}
                  {banner.audience === "CANDIDATES_ELIGIBLE_FOR_PREFERENCES" &&
                    "Eligible For Preferences"}
                </StyledTableCell>
                <StyledTableCell>
                  {banner.title && <strong>{banner.title}</strong>}
                  <div>{banner.message}</div>
                </StyledTableCell>
                <StyledTableCell>
                  {banner.platforms?.map(platform => (
                    <StyledPlatformBadge key={platform}>
                      {platform}
                    </StyledPlatformBadge>
                  ))}
                </StyledTableCell>
                <StyledTableCell>{banner.variant}</StyledTableCell>
                <StyledTableCell>{formatDate(banner.startsAt)}</StyledTableCell>
                <StyledTableCell>{formatDate(banner.endsAt)}</StyledTableCell>
                <StyledTableCell>
                  <StyledActionButtons>
                    <RotaButtonIcon
                      icon="edit"
                      onClick={() => handleEdit(banner)}
                    />
                    <RotaButtonIcon
                      icon="delete"
                      onClick={() => handleDelete(banner.id)}
                    />
                  </StyledActionButtons>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </tbody>
        </StyledTable>
      )}

      <BannerFormModal
        key={editingBanner?.id || "new"}
        banner={editingBanner}
        onClose={handleModalClose}
        onSuccess={handleSaveSuccess}
        isOpen={isModalOpen}
      />
    </StyledBannerContainer>
  );
};

export default FeatureBanners;
